import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { BruloHeader } from "@/components/brulo-header";
import { BruloMap, escapeHtml, safeHttpUrl, type MapPoint } from "@/components/brulo-map";
import { supabase } from "@/integrations/supabase/client";
import { buildCafeAddress } from "@/lib/mexican-states";
import { MapPin } from "lucide-react";

export const Route = createFileRoute("/mapa")({
  head: () => ({
    meta: [
      { title: "Mapa Brulo — cafés y eventos en Aguascalientes" },
      {
        name: "description",
        content:
          "Mapa interactivo de los puntos Brulo: cafeterías afiliadas y próximos eventos de running y ciclismo en Aguascalientes.",
      },
      { property: "og:title", content: "Mapa Brulo" },
      {
        property: "og:description",
        content: "Cafés afiliados y eventos próximos en un mapa interactivo.",
      },
    ],
  }),
  component: MapaPage,
});

type Cafe = {
  id: string;
  name: string;
  location: string;
  calle: string | null;
  numero_exterior: string | null;
  colonia: string | null;
  codigo_postal: string | null;
  city: string | null;
  estado: string | null;
  hours: string | null;
  opens_at: string | null;
  closes_at: string | null;
  member_discount: string | null;
  google_maps_url: string | null;
  latitude: number | null;
  longitude: number | null;
};


type EventRow = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  meeting_point: string;
  cafe_id: string | null;
  cancelado: boolean | null;
  estado_confirmacion: string | null;
  clubs: { name: string } | null;
};

type Filter = "all" | "cafes" | "events";

function MapaPage() {
  const [filter, setFilter] = useState<Filter>("all");
  const [cafes, setCafes] = useState<Cafe[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);

  useEffect(() => {
    (async () => {
      const today = new Date().toISOString().split("T")[0];
      const [cf, ev] = await Promise.all([
        supabase
          .from("cafes")
          .select(
            "id,name,location,calle,numero_exterior,colonia,codigo_postal,city,estado,hours,opens_at,closes_at,member_discount,google_maps_url,latitude,longitude",
          ),

        supabase
          .from("events")
          .select(
            "id,name,event_date,event_time,meeting_point,cafe_id,cancelado,estado_confirmacion,clubs(name)",
          )
          .gte("event_date", today),
      ]);
      setCafes((cf.data ?? []) as unknown as Cafe[]);
      setEvents(
        ((ev.data ?? []) as unknown as EventRow[]).filter(
          (e) => !e.cancelado && e.estado_confirmacion !== "declinado",
        ),
      );
    })();
  }, []);

  const points = useMemo<MapPoint[]>(() => {
    const cafeAddr = (c: Cafe) =>
      buildCafeAddress({
        calle: c.calle,
        numero_exterior: c.numero_exterior,
        colonia: c.colonia,
        codigo_postal: c.codigo_postal,
        city: c.city,
        estado: c.estado,
        location: c.location,
      });
    const cafeById = new Map(cafes.map((c) => [c.id, c]));
    const cafePts: MapPoint[] = cafes.map((c) => ({
      id: `cafe-${c.id}`,
      kind: "cafe",
      title: c.name,
      address: cafeAddr(c),
      html: cafePopup(c),
      coords:
        c.latitude != null && c.longitude != null
          ? { lat: c.latitude, lng: c.longitude }
          : null,
    }));
    const evPts: MapPoint[] = events
      .map((e): MapPoint | null => {
        const cafe = e.cafe_id ? cafeById.get(e.cafe_id) : null;
        const address = cafe
          ? cafeAddr(cafe)
          : e.meeting_point
            ? `${e.meeting_point}, Aguascalientes, México`
            : null;
        if (!address) return null;
        return {
          id: `evt-${e.id}`,
          kind: "event",
          title: e.name,
          address,
          html: eventPopup(e, cafe ?? null),
          coords:
            cafe && cafe.latitude != null && cafe.longitude != null
              ? { lat: cafe.latitude, lng: cafe.longitude }
              : null,
        };
      })
      .filter((x): x is MapPoint => x !== null);

    const list: MapPoint[] = [];
    if (filter !== "events") list.push(...cafePts);
    if (filter !== "cafes") list.push(...evPts);
    return list;
  }, [cafes, events, filter]);

  return (
    <div className="min-h-screen brulo-grain">
      <BruloHeader />
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 space-y-6">
        <div>
          <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">explora</p>
          <h1 className="font-display text-4xl sm:text-5xl">Mapa Brulo</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Encuentra los puntos Brulo de la ciudad: cafeterías afiliadas y los próximos
            eventos de la comunidad.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {(
            [
              ["all", "Todos"],
              ["cafes", "Solo cafés"],
              ["events", "Solo eventos"],
            ] as const
          ).map(([k, l]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-4 py-2 min-h-10 rounded-md text-sm font-semibold transition ${
                filter === k
                  ? "bg-primary text-primary-foreground"
                  : "bg-card border border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <BruloMap points={points} height={600} />

        <div className="flex items-center gap-5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ background: "#D85A30", border: "2px solid #5a1f0d" }}
            />
            Cafetería Brulo
          </span>
          <span className="inline-flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ background: "#fff", border: "2px solid #1a1a1a" }}
            />
            Evento próximo
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> Aguascalientes
          </span>
        </div>
      </div>
    </div>
  );
}

function cafePopup(c: Cafe): string {
  const hours =
    c.opens_at && c.closes_at
      ? `${c.opens_at.slice(0, 5)} – ${c.closes_at.slice(0, 5)}`
      : (c.hours ?? "");
  const fallback = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${c.location}, Aguascalientes`,
  )}`;
  const dir = safeHttpUrl(c.google_maps_url) ?? fallback;
  return `<div style="font-family:system-ui;max-width:240px;color:#111">
    <div style="font-weight:700;font-size:15px;margin-bottom:4px">${escapeHtml(c.name)}</div>
    <div style="color:#555;font-size:13px;margin-bottom:6px">${escapeHtml(c.location)}</div>
    ${hours ? `<div style="font-size:12px;color:#555;margin-bottom:4px">⏰ ${escapeHtml(hours)}</div>` : ""}
    ${c.member_discount ? `<div style="font-size:12px;color:#D85A30;font-weight:600;margin-bottom:6px">${escapeHtml(c.member_discount)} miembros Brulo</div>` : ""}
    <a href="${escapeHtml(dir)}" target="_blank" rel="noopener" style="display:inline-block;margin-top:6px;padding:6px 12px;background:#D85A30;color:#fff;text-decoration:none;border-radius:6px;font-size:12px;font-weight:600">Cómo llegar</a>
  </div>`;
}

function eventPopup(e: EventRow, c: Cafe | null): string {
  const date = new Date(`${e.event_date}T00:00:00`).toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return `<div style="font-family:system-ui;max-width:240px;color:#111">
    ${e.clubs?.name ? `<div style="color:#D85A30;font-size:10px;letter-spacing:.18em;text-transform:uppercase;font-weight:700;margin-bottom:4px">${escapeHtml(e.clubs.name)}</div>` : ""}
    <div style="font-weight:700;font-size:15px;margin-bottom:4px">${escapeHtml(e.name)}</div>
    <div style="font-size:13px;color:#555;margin-bottom:2px">${escapeHtml(date)} · ${escapeHtml(e.event_time?.slice(0, 5) ?? "")}</div>
    <div style="font-size:12px;color:#555;margin-bottom:6px">${escapeHtml(c?.name ?? e.meeting_point)}</div>
    <a href="/evento/${e.id}" style="display:inline-block;margin-top:4px;padding:6px 12px;background:#111;color:#fff;text-decoration:none;border-radius:6px;font-size:12px;font-weight:600">Unirme</a>
  </div>`;
}
